import { DEFAULT_MAX_GUESTS } from '../config'
import { Row } from '../schema/document'
import { MapHeight, PageElement } from '../schema/elements'
import {
  AboutVariant,
  ExtraBlock,
  GalleryVariant,
  HeroVariant,
  MenuVariant,
  SiteSettings,
  VisitVariant
} from '../schema/site'
import { MenuSnapshot } from '../render/menu'
import {
  ROW_ID,
  copyFor,
  everywhere,
  heading,
  headingCopy,
  imageElement,
  localizedParagraphs,
  paragraph,
  row,
  rowIdOfExtra
} from './shared'

/**
 * The sections, one builder each, shared by every template.
 *
 * A template is the order these come in, which variants it offers and how it
 * dresses them (`registry.ts`); it never builds a row itself. That is what
 * keeps three templates from being three copies of one file, and it is what
 * keeps the composition rule true everywhere: the reservation and the map,
 * which the schema insists stand alone in a single-column row, are built here
 * and nowhere else.
 */

export interface SectionContext {
  settings: SiteSettings
  languages: string[]
  defaultLanguage: string
  /** `formatAddress(settings)`, worked out once for the contact. */
  address: string
  /**
   * The venue's menu as it stands, or nothing. Not in the settings: it is
   * live data the page is drawn against, and a section that has no dishes to
   * show is a section that is not there.
   */
  menu?: MenuSnapshot | null
}

/** What a template may say about how a section is dressed. */
export interface SectionStyle {
  tone?: 'none' | 'surface' | 'muted' | 'accent'
  paddingY?: Row['paddingY']
  /** How dark the photo behind the name is tinted, where there is one. */
  overlay?: 'light' | 'medium' | 'strong'
  /** Photos across in the gallery grid. */
  columns?: 2 | 3 | 4
}

const background = (tone: SectionStyle['tone']): Row['background'] =>
  tone && tone !== 'none' ? { type: 'color', slot: tone } : { type: 'none' }

const textElement = (
  id: string,
  content: Record<string, ReturnType<typeof paragraph>>,
  align: 'left' | 'center' = 'left'
): PageElement => ({ id, type: 'text', align, content })

const heroTitle = (ctx: SectionContext, align: 'left' | 'center') => [
  heading(
    'tpl-hero-title',
    everywhere(ctx.languages, ctx.settings.name),
    align
  ),
  textElement(
    'tpl-tagline',
    localizedParagraphs(ctx.languages, ctx.settings.tagline),
    align
  ),
  ...(ctx.settings.phone
    ? [
        {
          id: 'tpl-phone',
          type: 'button' as const,
          label: headingCopy(ctx.languages, 'call'),
          target: {
            kind: 'url' as const,
            href: `tel:${ctx.settings.phone}`
          },
          variant: 'primary' as const,
          align
        }
      ]
    : [])
]

/**
 * The top of the page: the name, the line under it and the telephone.
 *
 * `photoBackground` puts the photo behind the words, `split` beside them and
 * `banner` under them. Without a photo the three collapse to the same thing, a
 * band of colour with the name on it, because a layout built around a photo
 * that is not there is a hole with a name in it.
 */
export function heroRows(
  ctx: SectionContext,
  variant: HeroVariant,
  style: SectionStyle = {}
): Row[] {
  const { photo } = ctx.settings.sections.hero
  const alt = everywhere(ctx.languages, ctx.settings.name)

  if (variant === 'split' && photo) {
    return [
      row(
        ROW_ID.hero,
        '1-1',
        [
          { id: 'tpl-col-hero', align: 'left', items: heroTitle(ctx, 'left') },
          {
            id: 'tpl-col-hero-photo',
            align: 'left',
            items: [imageElement('tpl-hero-photo', photo, alt, '4:3')]
          }
        ],
        {
          paddingY: style.paddingY ?? 'L',
          verticalAlign: 'center',
          background: background(style.tone)
        }
      )
    ]
  }

  if (variant === 'banner') {
    return [
      row(
        ROW_ID.hero,
        '1',
        [{ id: 'tpl-col-hero', align: 'center', items: heroTitle(ctx, 'center') }],
        {
          paddingY: style.paddingY ?? 'L',
          background: background(style.tone ?? 'surface')
        }
      ),
      ...(photo
        ? [
            row(
              `${ROW_ID.hero}-photo`,
              '1',
              [
                {
                  id: 'tpl-col-hero-photo',
                  align: 'center',
                  items: [imageElement('tpl-hero-photo', photo, alt, '16:9')]
                }
              ],
              { paddingY: 'S' }
            )
          ]
        : [])
    ]
  }

  return [
    row(
      ROW_ID.hero,
      '1',
      [{ id: 'tpl-col-hero', align: 'center', items: heroTitle(ctx, 'center') }],
      {
        paddingY: style.paddingY ?? 'L',
        background: photo
          ? { type: 'image', assetId: photo, overlay: style.overlay ?? 'medium' }
          : { type: 'color', slot: 'muted' }
      }
    )
  ]
}

/**
 * The menu, behind a button. One row and one element, because the element is
 * one of the full-width ones the composition rule keeps alone in a row; it
 * draws its own heading, its line and the button, and the sheet the button
 * opens. Nothing without dishes: a button onto an empty menu is a trap.
 */
export function menuRows(
  ctx: SectionContext,
  variant: MenuVariant,
  style: SectionStyle = {}
): Row[] {
  if (!ctx.menu || ctx.menu.categories.length === 0) return []
  const { showPrices, showPhotos } = ctx.settings.sections.menu
  return [
    row(
      ROW_ID.menu,
      '1',
      [
        {
          id: 'tpl-col-menu',
          /*
            Left, like every other section's heading on the page. Centred it
            was the one block that sat differently from the rest, and a page
            where one heading is centred and the five around it are not reads
            as a mistake rather than as an emphasis.
          */
          align: 'left',
          items: [
            {
              id: 'tpl-menu',
              type: 'menuFromPos',
              // Every category the venue has; the page is its whole menu.
              categoryIds: [],
              layout: variant,
              showPrices,
              showPhotos
            }
          ]
        }
      ],
      {
        background: background(style.tone),
        ...(style.paddingY ? { paddingY: style.paddingY } : {})
      }
    )
  ]
}

/** The description, and the photo the owner put beside it. */
export function aboutRows(
  ctx: SectionContext,
  variant: AboutVariant,
  style: SectionStyle = {}
): Row[] {
  const { photo } = ctx.settings.sections.about
  const words = [
    heading('tpl-about-heading', headingCopy(ctx.languages, 'about')),
    textElement(
      'tpl-about-text',
      localizedParagraphs(ctx.languages, ctx.settings.description)
    )
  ]
  const extra: Partial<Row> = {
    background: background(style.tone),
    ...(style.paddingY ? { paddingY: style.paddingY } : {})
  }
  const textColumn = { id: 'tpl-col-about-text', align: 'left' as const }

  if (!photo) {
    return [row(ROW_ID.about, '1', [{ ...textColumn, items: words }], extra)]
  }

  const alt = everywhere(ctx.languages, ctx.settings.name)

  if (variant === 'photoAbove') {
    return [
      row(
        ROW_ID.about,
        '1',
        [
          {
            ...textColumn,
            items: [imageElement('tpl-about-photo', photo, alt, '16:9'), ...words]
          }
        ],
        extra
      )
    ]
  }

  const photoColumn = {
    id: 'tpl-col-about-photo',
    align: 'left' as const,
    items: [imageElement('tpl-about-photo', photo, alt, '4:3')]
  }

  return variant === 'photoRight'
    ? [
        row(
          ROW_ID.about,
          '2-1',
          [{ ...textColumn, items: words }, photoColumn],
          extra
        )
      ]
    : [
        row(
          ROW_ID.about,
          '1-2',
          [photoColumn, { ...textColumn, items: words }],
          extra
        )
      ]
}

/** Opening hours and how to reach the place, side by side or one under the other. */
export function visitRows(
  ctx: SectionContext,
  variant: VisitVariant,
  style: SectionStyle = {}
): Row[] {
  const hours: PageElement[] = [
    heading('tpl-hours-heading', headingCopy(ctx.languages, 'hours')),
    {
      id: 'tpl-hours',
      type: 'openingHours',
      source: 'manual',
      entries: ctx.settings.hours
    }
  ]
  const contact: PageElement[] = [
    heading('tpl-contact-heading', headingCopy(ctx.languages, 'contact')),
    textElement(
      'tpl-address',
      Object.fromEntries(
        ctx.languages.map(language => [language, paragraph(ctx.address)])
      )
    ),
    ...(ctx.settings.email
      ? [
          {
            id: 'tpl-email',
            type: 'button' as const,
            label: headingCopy(ctx.languages, 'write'),
            target: {
              kind: 'url' as const,
              href: `mailto:${ctx.settings.email}`
            },
            variant: 'outline' as const,
            align: 'left' as const
          }
        ]
      : [])
  ]
  const extra: Partial<Row> = {
    background: background(style.tone),
    ...(style.paddingY ? { paddingY: style.paddingY } : {})
  }

  return variant === 'stacked'
    ? [
        row(
          ROW_ID.visit,
          '1',
          [{ id: 'tpl-col-visit', align: 'left', items: [...hours, ...contact] }],
          extra
        )
      ]
    : [
        row(
          ROW_ID.visit,
          '1-1',
          [
            { id: 'tpl-col-hours', align: 'left', items: hours },
            { id: 'tpl-col-contact', align: 'left', items: contact }
          ],
          extra
        )
      ]
}

/**
 * The photos of the place. A grid under its heading, or a strip the width of
 * the page with no heading at all: the strip is a band of pictures between
 * sections, and a heading over it would make it a section it is not.
 */
export function galleryRows(
  ctx: SectionContext,
  variant: GalleryVariant,
  style: SectionStyle = {}
): Row[] {
  const { photos } = ctx.settings.sections.gallery
  if (photos.length === 0) return []

  if (variant === 'strip') {
    return [
      row(
        ROW_ID.gallery,
        '1',
        [
          {
            id: 'tpl-col-gallery',
            align: 'left',
            items: [
              {
                id: 'tpl-gallery',
                type: 'gallery',
                assetIds: photos,
                columns: 4,
                lightbox: true
              }
            ]
          }
        ],
        {
          maxWidth: 'full',
          paddingY: 'S',
          gap: 'S',
          background: background(style.tone)
        }
      )
    ]
  }

  return [
    row(
      ROW_ID.gallery,
      '1',
      [
        {
          id: 'tpl-col-gallery',
          align: 'left',
          items: [
            heading('tpl-gallery-heading', headingCopy(ctx.languages, 'gallery')),
            {
              id: 'tpl-gallery',
              type: 'gallery',
              assetIds: photos,
              columns: style.columns ?? 3,
              lightbox: true
            }
          ]
        }
      ],
      {
        background: background(style.tone),
        ...(style.paddingY ? { paddingY: style.paddingY } : {})
      }
    )
  ]
}

/**
 * The venue's own rooms, and a table in one of them booked from the page.
 *
 * The plan is the picker: a guest chooses a table on it and the form under it
 * takes the rest (PLAN.md §5, mode B). The form posts to a route the server
 * does not answer yet, which is the renderer's seam and not the template's
 * concern; what the template owns is the words, and the consent line names the
 * venue because the venue is the one holding the data.
 *
 * Two rows, and the heading is why. The reservation block is one of the
 * full-width blocks the composition rule keeps ALONE in its column, so a
 * heading cannot stand above it inside one; the pair is a heading row and a
 * room row instead, both padded short so they read as one band.
 */
const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/

/**
 * The window the booking form offers, from the earliest hour the venue opens
 * in a week to the latest it closes. Nothing if it never opens.
 *
 * One window for all seven days, because the form has no script to redraw its
 * list with when the date changes; the venue confirms the booking anyway, and
 * an hour the kitchen is shut is a telephone call, not a lost guest.
 */
function bookingWindow(
  settings: SectionContext['settings']
): { from: string; to: string } | undefined {
  const open = settings.hours.filter(entry => !entry.closed)
  const from = open.map(entry => entry.opensAt).filter(value => HH_MM.test(value))
  const to = open.map(entry => entry.closesAt).filter(value => HH_MM.test(value))
  if (from.length === 0) return undefined
  return {
    from: from.sort()[0],
    to: to.length > 0 ? to.sort()[to.length - 1] : '23:45'
  }
}

export function reservationRows(ctx: SectionContext): Row[] {
  const rooms = (ctx.settings.reservationRooms ?? []).filter(
    room => room.tables.length > 0
  )
  if (rooms.length === 0) return []

  const name = ctx.settings.name.trim()

  return [
    row(
      `${ROW_ID.reservations}-heading`,
      '1',
      [
        {
          id: 'tpl-col-room-heading',
          align: 'left',
          items: [
            // Named for what a guest does here, not for what is drawn: the
            // plan is the way to book a table, and "our rooms" over it read as
            // a tour of the premises.
            heading('tpl-room-heading', headingCopy(ctx.languages, 'reservations')),
            /*
              Why the plan is here at all. Without a line saying so it is a
              diagram in the middle of a restaurant's website and a guest is
              left to guess: the drawing is where they will sit, and picking a
              table on it is the first step of booking one.
            */
            textElement(
              'tpl-room-note',
              localizedParagraphs(
                ctx.languages,
                Object.fromEntries(
                  ctx.languages.map(language => [
                    language,
                    copyFor(language, 'roomNote')
                  ])
                )
              )
            )
          ]
        }
      ],
      { paddingY: 'S' }
    ),
    row(
      ROW_ID.reservations,
      '1',
      [
        {
          id: 'tpl-col-room',
          align: 'left',
          items: [
            {
              id: 'tpl-reservation',
              type: 'reservation',
              variant: 'inline',
              rooms,
              label: headingCopy(ctx.languages, 'book'),
              /*
                Names the venue while it has one. The preview redraws on every
                keystroke of the form, name included, and with the name still
                blank the line read "by  in order to": the version that says
                "the venue" covers that gap.
              */
              consent: Object.fromEntries(
                ctx.languages.map(language => [
                  language,
                  name
                    ? copyFor(language, 'consent').replace('{name}', name)
                    : copyFor(language, 'consentNoName')
                ])
              ),
              maxGuests: DEFAULT_MAX_GUESTS,
              /*
                The hours the form offers and the one it opens on: the venue's
                own window, and its start. One hour has to stand for the week
                and the earliest is the only one a guest can always move
                forward from; a form that opened at closing time would be
                asking everyone to correct it.
              */
              ...(bookingWindow(ctx.settings)
                ? {
                    bookingHours: bookingWindow(ctx.settings),
                    defaultTime: bookingWindow(ctx.settings)?.from
                  }
                : {}),
              // For the guest who would rather ring; the form is the booking.
              ...(ctx.settings.phone ? { phone: ctx.settings.phone } : {})
            }
          ]
        }
      ],
      { paddingY: 'S' }
    )
  ]
}

/**
 * Where the place is. A map owns its row: the composition rule keeps it alone.
 *
 * Built whether or not the venue is connected to Google. Without a place the
 * map has no URL, and the renderer decides what that means: a placeholder
 * pointing at the settings in the editor, nothing at all on a published page.
 */
export function mapRows(ctx: SectionContext, height: MapHeight): Row[] {
  return [
    row(ROW_ID.map, '1', [
      {
        id: 'tpl-col-map',
        align: 'left',
        items: [
          {
            id: 'tpl-map',
            type: 'map',
            embedUrl: ctx.settings.googleLocation?.embedUrl ?? '',
            height
          }
        ]
      }
    ])
  ]
}

/**
 * A button to the venue's Google review form, in the owner's words or the
 * template's. Only with a connected place: a button to nowhere is not a
 * section. It opens beside the page, which the guest is still on.
 */
export function reviewRows(
  ctx: SectionContext,
  label: Record<string, string> | undefined
): Row[] {
  const href = ctx.settings.googleLocation?.reviewUrl
  if (!href) return []
  return [
    row(
      ROW_ID.review,
      '1',
      [
        {
          id: 'tpl-col-review',
          align: 'center',
          items: [
            {
              id: 'tpl-review',
              type: 'button',
              label: Object.fromEntries(
                ctx.languages.map(language => [
                  language,
                  label?.[language]?.trim() || copyFor(language, 'review')
                ])
              ),
              target: { kind: 'url', href, newTab: true },
              variant: 'outline',
              align: 'center'
            }
          ]
        }
      ],
      { paddingY: 'S' }
    )
  ]
}

/**
 * One of the owner's own blocks. A heading if they wrote one, their text, and
 * their photo beside it on the side they chose; without a photo the text takes
 * the width. The photo's alt is the heading, or the venue's name when there is
 * none, because a page with a photo nobody can read is refused at publication.
 */
export function extraRows(ctx: SectionContext, extra: ExtraBlock): Row[] {
  const hasHeading = Object.values(extra.heading ?? {}).some(
    value => value && value.trim()
  )
  const words: PageElement[] = [
    ...(hasHeading
      ? [
          heading(
            `tpl-extra-${extra.id}-heading`,
            Object.fromEntries(
              ctx.languages.map(language => [
                language,
                extra.heading?.[language] ?? ''
              ])
            )
          )
        ]
      : []),
    textElement(
      `tpl-extra-${extra.id}-text`,
      localizedParagraphs(ctx.languages, extra.text)
    )
  ]
  const textColumn = {
    id: `tpl-col-extra-${extra.id}-text`,
    align: 'left' as const,
    items: words
  }

  if (!extra.photo) {
    return [row(rowIdOfExtra(extra.id), '1', [textColumn])]
  }

  const alt = Object.fromEntries(
    ctx.languages.map(language => [
      language,
      extra.heading?.[language]?.trim() || ctx.settings.name
    ])
  )
  const photoColumn = {
    id: `tpl-col-extra-${extra.id}-photo`,
    align: 'left' as const,
    items: [
      imageElement(`tpl-extra-${extra.id}-photo`, extra.photo, alt, '4:3')
    ]
  }

  return extra.photoSide === 'right'
    ? [row(rowIdOfExtra(extra.id), '2-1', [textColumn, photoColumn])]
    : [row(rowIdOfExtra(extra.id), '1-2', [photoColumn, textColumn])]
}
